import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, CircularProgress } from "@mui/material";
import {
  getFinancialsTableOptions,
  getFinancialsTableQueryKey,
  patchFinancialsTable,
  useHeyApi,
} from "@platform/api";
import {
  useOptimisticAutosave,
  useApiErrorHandler,
  useAgGridViewStorage,
  useMpxAgGridState,
} from "@platform/hooks";

import { FinancialsGrid } from "./grid/FinancialsGrid";
import { alignPersistedCarrierColumnOrder } from "./grid/alignPersistedCarrierColumnOrder";
import { applyCarrierColumnOrder } from "./grid/applyCarrierColumnOrder";
import {
  buildAnnualPremiumDiff,
  buildToggleCarrierHiddenDiff,
} from "./grid/buildPatchDiff";
import {
  applyFinancialsDiff,
  mergePendingFinancialsChanges,
} from "./grid/mergeFinancialsPatch";
import { EmptyTotalsBlocksBanner } from "./EmptyTotalsBlocksBanner";
import { findEmptyTotalsBlockNames } from "./emptyTotalsBlocks";
import { useFinancialsHiddenCarriers } from "./useFinancialsHiddenCarriers";

import type {
  CarrierColumn,
  FinancialsTableResponse,
  PatchFinancialsTableRequest,
} from "@platform/api";
import type { ColumnContext } from "./grid/valueGetters";
import type { MpxAgGridStateProps } from "@platform/hooks";

interface FinancialsTabProps {
  rfpId: string;
  carrierOrder?: string[];
  premiumOverridesEnabled: boolean;
}

// Trim off the measured height so the grid clears the fixed bottom chrome (the
// carrier-notes bar) with ~1rem of margin above it, and the page doesn't scroll.
const GRID_BOTTOM_OFFSET_PX = 64; // 4rem

export const FinancialsTab = ({
  rfpId,
  carrierOrder,
  premiumOverridesEnabled,
}: FinancialsTabProps): React.JSX.Element => {
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () =>
      getFinancialsTableQueryKey({
        path: { id: rfpId },
        client: centroPortalClient,
      }),
    [rfpId, centroPortalClient],
  );

  const handleQueryError = useApiErrorHandler({
    fallback: "Failed to load the financials table.",
  });

  const handleSaveError = useApiErrorHandler({
    fallback: "Failed to save your financials changes.",
  });

  const {
    data: payload,
    error,
    isLoading,
  } = useQuery(
    getFinancialsTableOptions({
      path: { id: rfpId },
      client: centroPortalClient,
    }),
  );

  if (error) handleQueryError(error as Error);

  const { queueChange, flushNow, status } = useOptimisticAutosave<
    PatchFinancialsTableRequest,
    void,
    FinancialsTableResponse
  >({
    queryKey,
    applyDiff: applyFinancialsDiff,
    mergeChanges: mergePendingFinancialsChanges,
    onSave: async (body) => {
      await patchFinancialsTable({
        path: { id: rfpId },
        body,
        client: centroPortalClient,
        throwOnError: true,
      });
    },
  });

  // Surface cell-edit autosave failures; the hook reverts the cache but
  // leaves notifying the user to the caller.
  useEffect(() => {
    if (status.status === "error") handleSaveError(status.lastError);
  }, [status, handleSaveError]);

  // Structural totals-block changes (create/delete) bypass the optimistic
  // autosave: each is a discrete write whose result we can't predict
  // locally (the backend assigns the new block's id), so we flush any
  // pending cell edits, persist the change, then refetch authoritative state.
  const runStructuralChange = useCallback(
    (body: PatchFinancialsTableRequest): void => {
      void (async () => {
        try {
          await flushNow();
          await patchFinancialsTable({
            path: { id: rfpId },
            body,
            client: centroPortalClient,
            throwOnError: true,
          });
          await queryClient.invalidateQueries({ queryKey });
        } catch (saveError) {
          handleSaveError(saveError as Error);
        }
      })();
    },
    [
      flushNow,
      rfpId,
      centroPortalClient,
      queryClient,
      queryKey,
      handleSaveError,
    ],
  );

  const createTotalsBlock = useCallback(
    (label: string, coverageIds: string[], position: number): void =>
      runStructuralChange({
        newTotalsBlocks: [{ label, position, coverageIds }],
      }),
    [runStructuralChange],
  );

  // Reverting an override hands the cell back to the premium calculated from
  // the Quotes rates, which only the server knows, so it saves and refetches
  // rather than editing the cache.
  const revertPremium = useCallback(
    (coverageId: string, column: ColumnContext): void =>
      runStructuralChange(buildAnnualPremiumDiff(column, coverageId, null)),
    [runStructuralChange],
  );

  const deleteTotalsBlock = useCallback(
    (blockId: string): void =>
      runStructuralChange({ deleteTotalsBlockIds: [blockId] }),
    [runStructuralChange],
  );

  const { gridProps: mpxGridProps, isLoading: isLoadingGridState } =
    useMpxAgGridState({
      scopeType: "rfp",
      scopeId: rfpId,
      gridId: "financials-table",
    });

  const viewGridProps: MpxAgGridStateProps = useAgGridViewStorage({
    storageKey: `financials-grid-view:${rfpId}`,
    gridProps: mpxGridProps,
    persist: { columnVisibility: false },
  });

  // The order every tab shares: the analyst's Quotes-page sequence when set,
  // else alphabetical by carrier name. Resolved once and applied both to the
  // grid's persisted AG Grid column state and to the row data below, so
  // neither an old persisted state nor a local in-grid drag can stand in as
  // "the order" — only a Quotes reorder ever changes carrierOrder itself.
  const resolvedCarrierOrder = useMemo(
    () =>
      carrierOrder && carrierOrder.length > 0
        ? carrierOrder
        : payload?.carriers
            .toSorted((a, b) => a.name.localeCompare(b.name))
            .map((carrier) => carrier.id),
    [carrierOrder, payload],
  );

  const gridProps: MpxAgGridStateProps = useMemo(
    () => ({
      ...viewGridProps,
      initialState: alignPersistedCarrierColumnOrder(
        viewGridProps.initialState,
        resolvedCarrierOrder,
      ),
    }),
    [viewGridProps, resolvedCarrierOrder],
  );

  const { reconcilePayload, persistHiddenCarrier } =
    useFinancialsHiddenCarriers(rfpId);

  const reconciledPayload = useMemo(() => {
    if (!payload) return undefined;
    const reconciled = reconcilePayload(payload);
    return {
      ...reconciled,
      carriers: applyCarrierColumnOrder(
        reconciled.carriers,
        resolvedCarrierOrder,
      ),
    };
  }, [payload, reconcilePayload, resolvedCarrierOrder]);

  const toggleCarrierHidden = useCallback(
    (
      carrierId: string,
      currentlyHidden: boolean,
      carriers: readonly CarrierColumn[],
    ): void => {
      persistHiddenCarrier(carriers, carrierId, !currentlyHidden);
      queueChange(
        buildToggleCarrierHiddenDiff(carrierId, currentlyHidden, carriers),
      );
    },
    [persistHiddenCarrier, queueChange],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState<number>();

  useLayoutEffect(() => {
    const measure = (): void => {
      const el = containerRef.current;
      if (!el) return;

      const { top } = el.getBoundingClientRect();
      const parent = el.parentElement;
      const parentStyle = parent ? getComputedStyle(parent) : null;
      const toPx = (value: string): number => parseFloat(value) || 0;
      const insetBelow = parentStyle
        ? toPx(parentStyle.paddingBottom) + toPx(parentStyle.marginBottom)
        : 0;

      setAvailableHeight(
        Math.max(
          0,
          window.innerHeight - top - insetBelow - GRID_BOTTOM_OFFSET_PX,
        ),
      );
    };

    measure();
    window.addEventListener("resize", measure);

    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <Box
      ref={containerRef}
      data-testid="financials-tab"
      sx={{
        height: availableHeight,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {isLoading || !reconciledPayload || isLoadingGridState ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isLoading || isLoadingGridState ? <CircularProgress /> : null}
        </Box>
      ) : (
        <>
          <EmptyTotalsBlocksBanner
            blockNames={findEmptyTotalsBlockNames(reconciledPayload)}
          />
          <FinancialsGrid
            payload={reconciledPayload}
            queueChange={queueChange}
            toggleCarrierHidden={toggleCarrierHidden}
            createTotalsBlock={createTotalsBlock}
            deleteTotalsBlock={deleteTotalsBlock}
            revertPremium={revertPremium}
            premiumOverridesEnabled={premiumOverridesEnabled}
            gridProps={gridProps}
          />
        </>
      )}
    </Box>
  );
};
